import { useState, useEffect, useCallback } from 'react';
import { useAssignedForms } from 'src/hooks/useAssignedForms';
import FloatingAddLog from 'src/features/logs/components/FloatingAddLog';
import LogForm from 'src/features/logs/components/LogForm';
import parseLogPayload from 'src/features/logs/utils/parseLogPayload';
import getAttachments from 'src/features/logs/utils/getAttachments';
import { useSnackbar } from 'notistack';
import { MenuPopoverItem } from 'src/components';
import { LogsService } from 'src/services/logs/logs.service';
import { AssetService } from 'src/services/assets/asset.service';

interface Props {
  customerId: string;
  siteId: string;
}

export const FloatingAddLogWrapper = ({ customerId, siteId }: Props) => {
  const { forms, loading, hasForms } = useAssignedForms(customerId, siteId);
  const { enqueueSnackbar } = useSnackbar();

  const [activeFormId, setActiveFormId] = useState<string | null>(null);
  const [isCreatingLog, setIsCreatingLog] = useState(false);
  const [isDeletingLog, setIsDeletingLog] = useState(false);
  const [customerAssets, setCustomerAssets] = useState<any[]>([]);

  const form = forms.find((f) => f.id === activeFormId);

  const onClose = useCallback(() => {
    setActiveFormId(null);
  }, []);

  useEffect(() => {
    if (!customerId || !siteId) return;

    AssetService.getAll(customerId, siteId)
      .then(setCustomerAssets)
      .catch((err) => console.error(err));
  }, [customerId, siteId]);

  const handleCreateLog = async (data: any, logFormId?: string, allowComments?: boolean) => {
    if (!form?.id && !logFormId) return;

    const log: any = parseLogPayload(
      data,
      (form?.id || logFormId) as string,
      !!allowComments,
      undefined
    );

    const attachments = getAttachments(data);
    const maxSizeInBytes = 10 * 1024 * 1024;
    let hasTooLargeFile = false;

    Object.values(attachments).forEach((arr: any) => {
      if (Array.isArray(arr)) {
        arr.forEach((file: any) => {
          if (file?.size > maxSizeInBytes) hasTooLargeFile = true;
        });
      }
    });

    if (hasTooLargeFile) {
      return enqueueSnackbar('Uploaded file size is too long! (Maximum is: 10mb)', {
        variant: 'error',
      });
    }

    try {
      setIsCreatingLog(true);
      await LogsService.add(customerId, siteId, log, attachments);
      enqueueSnackbar('Log successfully created!', { variant: 'success' });
      setIsCreatingLog(false);
      onClose();
    } catch (err) {
      enqueueSnackbar('Log creation failed!', { variant: 'error' });
      setIsCreatingLog(false);
    }
  };

  const handleDeleteLog = async (logIdToDelete: string) => {
    try {
      setIsDeletingLog(true);
      await LogsService.patch(customerId, siteId, logIdToDelete, { status: 'inactive' });
      enqueueSnackbar('Log successfully deleted!', { variant: 'success' });
      setIsDeletingLog(false);
      onClose();
    } catch (err) {
      enqueueSnackbar('Log deletion failed!', { variant: 'error' });
      setIsDeletingLog(false);
    }
  };

  const handleChangeVersionHistory = (historyItem: any, lastItem: any) => {};

  return (
    <>
      {activeFormId && form && (
        <LogForm
          onChangeVersionHistory={handleChangeVersionHistory}
          version={undefined}
          isCreatingLog={isCreatingLog}
          isDeletingLog={isDeletingLog}
          logId={undefined}
          onSubmit={handleCreateLog}
          onDelete={handleDeleteLog}
          onClose={onClose}
          isDefault={form?.isDefault}
          id={form?.id}
          customerAssets={customerAssets}
        />
      )}

      {hasForms && (
        <FloatingAddLog
          disabled={loading || !hasForms}
          renderActions={(onClosePopover) => (
            <>
              {forms.map((f) => (
                <MenuPopoverItem
                  key={f.id}
                  handleClick={() => {
                    onClosePopover();
                    setActiveFormId(f.id);
                  }}
                  customContent={<>{f.name}</>}
                />
              ))}
            </>
          )}
        />
      )}
    </>
  );
};
